
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUserFilters } from "./useUserFilters";

interface CommodityGroupMapping {
  [key: string]: string[];
}

// Mapping of commodity groups to keywords that might appear in report names
const COMMODITY_GROUP_KEYWORDS: CommodityGroupMapping = {
  'Tree Fruits': ['fruit', 'apple', 'pear', 'peach', 'cherry', 'plum'],
  'Tree Nuts': ['nut', 'almond', 'walnut', 'pecan', 'brazil', 'hazelnut'],
  'Field Crops': ['corn', 'soybean', 'wheat', 'cotton', 'rice'],
  'Vegetables': ['vegetable', 'tomato', 'lettuce', 'onion', 'carrot', 'potato'],
  'Root Crops': ['potato', 'sweet potato', 'carrot', 'beet', 'turnip'],
  'Small Grains': ['wheat', 'barley', 'oats', 'rye', 'rice'],
  'Leafy Greens': ['lettuce', 'spinach', 'kale', 'arugula', 'cabbage'],
  'Specialty Crops': ['tobacco', 'hops', 'herbs', 'spice']
};

export const useProfilePriceMatching = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { userCrops, userProfile } = useUserFilters();

  const matchAndFetchProfilePrices = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🎯 Starting enhanced smart profile price matching with date filtering...');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user found');
      }

      // Get user profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, location')
        .eq('user_id', user.id)
        .single();

      if (!profile) {
        throw new Error('No profile found for user');
      }

      console.log('📍 User location:', profile.location);
      console.log('🌾 User crops:', userCrops);

      // Calculate date range for the past month
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      const today = new Date();

      console.log('📅 Date filter range:', oneMonthAgo.toISOString(), 'to', today.toISOString());

      // Step 1: Get profile reports with Terminal Market and recent dates
      const { data: profileReports, error: reportsError } = await supabase
        .from('profile_reports')
        .select('id, report_name, market_types, market_city, report_id')
        .eq('profile_id', profile.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (reportsError) {
        throw reportsError;
      }

      console.log(`📊 Found ${profileReports?.length || 0} profile reports`);

      if (!profileReports || profileReports.length === 0) {
        return {
          matchedReports: 0,
          storedPrices: 0,
          message: 'No profile reports found',
          dateRange: { from: oneMonthAgo, to: today },
          matches: []
        };
      }

      // Get USDA report details for the profile reports
      const reportIds = profileReports.map(pr => pr.report_id);
      const { data: usdaReports } = await supabase
        .from('usda_reports')
        .select('id, report_name, market_types, commodities, varieties, report_type, published_date')
        .in('id', reportIds)
        .gte('published_date', oneMonthAgo.toISOString())
        .lte('published_date', today.toISOString());

      const usdaReportsMap = new Map();
      usdaReports?.forEach(report => {
        usdaReportsMap.set(report.id, report);
      });

      // Filter for terminal market reports
      const terminalMarketReports = profileReports.filter(report => {
        const usdaReport = usdaReportsMap.get(report.report_id);
        if (!usdaReport) return false;

        const hasTerminalMarket = 
          (report.market_types && JSON.stringify(report.market_types).toLowerCase().includes('terminal')) ||
          (usdaReport.market_types && JSON.stringify(usdaReport.market_types).toLowerCase().includes('terminal')) ||
          (report.report_name && report.report_name.toLowerCase().includes('terminal')) ||
          (usdaReport.report_name && usdaReport.report_name.toLowerCase().includes('terminal'));
        
        return hasTerminalMarket;
      });

      console.log(`🏢 Found ${terminalMarketReports.length} recent terminal market reports`);

      // Step 2: Enhanced matching with date-filtered reports
      const enhancedMatches = [];

      for (const crop of userCrops) {
        console.log(`🔍 Processing crop: ${crop.commodity} - ${crop.variety || 'No variety'}`);
        
        // Find the commodity group for this crop
        const cropGroup = crop.group_name || getCommodityGroup(crop.commodity);
        const groupKeywords = COMMODITY_GROUP_KEYWORDS[cropGroup] || [];
        
        console.log(`📂 Group: ${cropGroup}, Keywords: ${groupKeywords.join(', ')}`);

        // Find terminal reports that match this commodity group
        for (const report of terminalMarketReports) {
          const usdaReport = usdaReportsMap.get(report.report_id);
          if (!usdaReport) continue;

          const reportName = (report.report_name || usdaReport.report_name || '').toLowerCase();
          const publishedDate = usdaReport.published_date;
          
          // Check if report name contains group keywords
          const hasGroupMatch = groupKeywords.some(keyword => 
            reportName.includes(keyword.toLowerCase())
          );
          
          // Check if report contains the specific commodity
          const hasCommodityMatch = 
            reportName.includes(crop.commodity.toLowerCase()) ||
            (usdaReport.commodities && 
             usdaReport.commodities.some((c: string) => 
               c.toLowerCase().includes(crop.commodity.toLowerCase())
             ));

          // Check if report contains the specific variety (if specified)
          const hasVarietyMatch = !crop.variety || 
            reportName.includes(crop.variety.toLowerCase()) ||
            (usdaReport.varieties && 
             usdaReport.varieties.some((v: string) => 
               v.toLowerCase().includes(crop.variety.toLowerCase())
             ));

          if (hasGroupMatch && hasCommodityMatch && hasVarietyMatch) {
            enhancedMatches.push({
              report,
              crop,
              matchType: 'full_match',
              publishedDate,
              reportName: report.report_name || usdaReport.report_name
            });
            console.log(`✅ Full match found: ${reportName} (${publishedDate}) for ${crop.commodity}-${crop.variety}`);
          } else if (hasGroupMatch && hasCommodityMatch) {
            enhancedMatches.push({
              report,
              crop,
              matchType: 'commodity_match',
              publishedDate,
              reportName: report.report_name || usdaReport.report_name
            });
            console.log(`🟡 Commodity match found: ${reportName} (${publishedDate}) for ${crop.commodity}`);
          } else if (hasGroupMatch) {
            enhancedMatches.push({
              report,
              crop,
              matchType: 'group_match',
              publishedDate,
              reportName: report.report_name || usdaReport.report_name
            });
            console.log(`🟠 Group match found: ${reportName} (${publishedDate}) for ${cropGroup}`);
          }
        }
      }

      console.log(`🎯 Total enhanced matches found: ${enhancedMatches.length}`);

      // Step 3: Fetch prices for matched reports with enhanced data
      const { data: response, error: fetchError } = await supabase.functions.invoke('fetch-usda-data', {
        body: { 
          fetchAndStoreProfilePrices: true,
          userLocation: profile.location,
          commodities: [...new Set(userCrops.map(c => c.commodity))],
          varieties: [...new Set(userCrops.map(c => c.variety).filter(Boolean))],
          matchedReports: enhancedMatches.map(m => ({
            reportId: m.report.id,
            reportName: m.reportName,
            commodity: m.crop.commodity,
            variety: m.crop.variety,
            matchType: m.matchType,
            publishedDate: m.publishedDate
          }))
        }
      });

      if (fetchError) {
        throw fetchError;
      }

      console.log('💰 Enhanced profile price matching completed:', response);
      
      return {
        matchedReports: enhancedMatches.length,
        storedPrices: response?.data?.length || 0,
        message: response?.message || 'Enhanced profile prices processed successfully',
        dateRange: { from: oneMonthAgo, to: today },
        matches: enhancedMatches
      };

    } catch (err) {
      console.error('❌ Error in enhanced profile price matching:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    matchAndFetchProfilePrices,
    loading,
    error
  };
};

// Helper function to determine commodity group from commodity name
function getCommodityGroup(commodity: string): string {
  const commodityLower = commodity.toLowerCase();
  
  const fruitKeywords = ['apple', 'pear', 'peach', 'cherry', 'plum', 'apricot', 'grape'];
  const nutKeywords = ['almond', 'walnut', 'pecan', 'brazil', 'hazelnut', 'cashew', 'pistachio'];
  const grainKeywords = ['wheat', 'barley', 'oats', 'rye', 'rice'];
  const fieldKeywords = ['corn', 'soybean', 'cotton'];
  const vegetableKeywords = ['tomato', 'lettuce', 'onion', 'carrot', 'cabbage'];
  const rootKeywords = ['potato', 'sweet potato', 'beet', 'turnip'];

  if (fruitKeywords.some(keyword => commodityLower.includes(keyword))) {
    return 'Tree Fruits';
  } else if (nutKeywords.some(keyword => commodityLower.includes(keyword))) {
    return 'Tree Nuts';
  } else if (grainKeywords.some(keyword => commodityLower.includes(keyword))) {
    return 'Small Grains';
  } else if (fieldKeywords.some(keyword => commodityLower.includes(keyword))) {
    return 'Field Crops';
  } else if (vegetableKeywords.some(keyword => commodityLower.includes(keyword))) {
    return 'Vegetables';
  } else if (rootKeywords.some(keyword => commodityLower.includes(keyword))) {
    return 'Root Crops';
  }
  
  return 'Specialty Crops'; // Default group
}
